import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
} from '@mui/material';
import {
    RestartAlt as RestartAltIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';

import { useProjects } from '../../../../../hooks/useProjects';
import { useServicesTypesProject } from '../../../../../hooks/administration/useServicesTypesProject';

const OrderServiceFilters = ({
    filters,
    onApply,
    onReset,
    isFetching,
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    const {
        projects = [],
        isLoading: loadingProjects,
    } = useProjects();

    const {
        servicesTypesProject = [],
        isLoading: loadingServices,
    } = useServicesTypesProject();

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const filteredServices = useMemo(() => {
        if (!localFilters.proID) return servicesTypesProject;

        return servicesTypesProject.filter((service) => {
            const serviceProjectId = service.proid || service.ProID;
            return String(serviceProjectId) === String(localFilters.proID);
        });
    }, [servicesTypesProject, localFilters.proID]);

    const handleChange = (field, value) => {
        setLocalFilters((prev) => {
            const next = {
                ...prev,
                [field]: value,
            };

            if (field === 'proID') {
                next.serProID = '';
            }

            return next;
        });
    };

    const handleApply = () => {
        onApply(localFilters);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        label="Fecha desde"
                        type="date"
                        fullWidth
                        size="small"
                        value={localFilters.dateFrom || ''}
                        onChange={(e) => handleChange('dateFrom', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        label="Fecha hasta"
                        type="date"
                        fullWidth
                        size="small"
                        value={localFilters.dateTo || ''}
                        onChange={(e) => handleChange('dateTo', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        select
                        label="Proyecto"
                        fullWidth
                        size="small"
                        value={localFilters.proID || ''}
                        onChange={(e) => handleChange('proID', e.target.value)}
                        disabled={loadingProjects}
                    >
                        <MenuItem value="">Todos los proyectos</MenuItem>

                        {projects.map((project) => {
                            const id = project.proid || project.ProID;
                            const name = project.pronom || project.ProNom;

                            return (
                                <MenuItem key={id} value={id}>
                                    {name}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        select
                        label="Servicio"
                        fullWidth
                        size="small"
                        value={localFilters.serProID || ''}
                        onChange={(e) => handleChange('serProID', e.target.value)}
                        disabled={loadingServices}
                    >
                        <MenuItem value="">Todos los servicios</MenuItem>

                        {filteredServices.map((service) => {
                            const id = service.serproid || service.SerProID;
                            const name = service.serpronom || service.SerProNom;
                            const projectName = service.pronom || service.ProNom;

                            return (
                                <MenuItem key={id} value={id}>
                                    {projectName ? `${name} · ${projectName}` : name}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </Grid>

                <Grid item xs={12} md={2}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            onClick={onReset}
                        >
                            Limpiar
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={handleApply}
                            disabled={isFetching}
                        >
                            Actualizar
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default OrderServiceFilters;